import { createFileRoute, Link, Navigate, useNavigate } from "@tanstack/react-router";
import { useRef, useState, type ReactNode } from "react";
import { ArrowRight, LayoutGrid, Menu, Play, Sparkles } from "lucide-react";

import { useBusinessOnboardingHost } from "@/business-onboarding/onboarding-host-context";
import { AccountMenu } from "@/components/AccountMenu";
import { HighlightedText } from "@/components/HighlightedText";
import { Logo } from "@/components/Logo";
import { ProductDrawer } from "@/components/ProductDrawer";
import { useDiscoveryHost } from "@/components/discovery-host-context";
import { brand, pageTitle } from "@/lib/brand";
import { useI18n } from "@/lib/i18n";
import { useManagedCompanies } from "@/auth/useManagedCompanies";
import { ErrorState } from "@/components/DataState";
import type { ManagedCompany } from "@/data/firestore/membershipRepository";
import { useInitialSplashReady } from "@/hooks/useInitialSplashReady";

const homeDescription =
  "Turn your website traffic into qualified leads with AI-guided discovery that helps visitors clarify their needs and explore suitable solutions.";

const homeStructuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://qotta.co/#organization",
      name: "Qotta",
      url: "https://qotta.co/",
      logo: "https://qotta.co/logo/qotta_logo.webp",
      description:
        "Qotta turns website traffic into qualified sales opportunities with AI-guided discovery and optional indicative estimates.",
      email: "info@qotta.co",
    },
    {
      "@type": "WebApplication",
      "@id": "https://qotta.co/#webapp",
      name: "Qotta",
      url: "https://qotta.co/",
      applicationCategory: "BusinessApplication",
      description:
        "Qotta helps turn website traffic into qualified sales opportunities through guided discovery and optional indicative estimates.",
      publisher: {
        "@id": "https://qotta.co/#organization",
      },
    },
  ],
} as const;

export const Route = createFileRoute("/")({
  validateSearch: (search: Record<string, unknown>): { view?: "public" } => search["view"] === "public" ? { view: "public" } : {},
  head: () => ({
    meta: [
      { title: pageTitle() },
      {
        name: "description",
        content: homeDescription,
      },
      { property: "og:title", content: pageTitle() },
      { property: "og:description", content: homeDescription },
      { property: "og:url", content: brand.url },
      { property: "og:image", content: brand.socialImageUrl },
      { property: "og:image:alt", content: brand.socialImageAlt },
      { name: "twitter:title", content: pageTitle() },
      { name: "twitter:description", content: homeDescription },
      { name: "twitter:image", content: brand.socialImageUrl },
      { name: "twitter:image:alt", content: brand.socialImageAlt },
    ],
    links: [{ rel: "canonical", href: brand.url }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(homeStructuredData),
      },
    ],
  }),
  component: HomeEntry,
});

function InitialSplashReady({ children }: { children: ReactNode }) {
  useInitialSplashReady();
  return children;
}

function HomeEntry() {
  const publicView = Route.useSearch().view === "public";
  const { currentUser, isSystemAdmin, adminError, resolving, companies, membershipError } = useManagedCompanies();

  if (resolving) {
    return <div className="min-h-screen bg-surface" role="status" aria-label="Checking account" />;
  }
  if (!currentUser || isSystemAdmin) return <Home />;
  if (adminError) return <InitialSplashReady><main className="min-h-screen p-6"><ErrorState error={adminError} /><AccountMenu /></main></InitialSplashReady>;
  if (membershipError) return <InitialSplashReady><main className="min-h-screen p-6"><ErrorState error={membershipError} /><AccountMenu /></main></InitialSplashReady>;
  if (companies === null) return <div className="min-h-screen bg-surface" role="status" aria-label="Checking companies" />;
  const isExistingManagerPublicView = publicView && companies.length > 0;
  if (publicView) return <Home managedCompanies={isExistingManagerPublicView ? companies : undefined} />;
  if (companies.length === 0) return <Home />;
  const onlyCompany = companies[0];
  if (companies.length === 1 && onlyCompany) return <Navigate to="/app/$customerId" params={{ customerId: onlyCompany.customerId }} replace />;
  return (
    <InitialSplashReady>
      <main className="mx-auto flex min-h-screen max-w-xl flex-col justify-center gap-6 p-6">
        <Logo size="large" />
        <h1 className="text-2xl font-bold">Choose a company</h1>
        <div className="grid gap-3">
          {companies.map((company) => (
            <Link key={company.customerId} to="/app/$customerId" params={{ customerId: company.customerId }} className="rounded-xl border border-border bg-card p-5 transition hover:border-primary">
              <span className="block font-semibold">{company.name}</span>
              {company.domain && <span className="block text-sm text-muted-foreground">{company.domain}</span>}
            </Link>
          ))}
        </div>
      </main>
    </InitialSplashReady>
  );
}

function Home({ managedCompanies }: { managedCompanies?: ManagedCompany[] | undefined }) {
  useInitialSplashReady();
  const { t } = useI18n();
  const navigate = useNavigate();
  const { openDiscovery } = useDiscoveryHost();
  const { openOnboarding, canOpenOnboarding } = useBusinessOnboardingHost();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const returnToDashboard = () => {
    const onlyCompany = managedCompanies?.length === 1 ? managedCompanies[0] : undefined;
    if (onlyCompany) void navigate({ to: "/app/$customerId", params: { customerId: onlyCompany.customerId } });
    else void navigate({ to: "/", search: {} });
  };

  const handleVideoPlay = () => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = 0;
    void video
      .play()
      .then(() => setIsVideoPlaying(true))
      .catch(() => setIsVideoPlaying(false));
  };

  const handleVideoEnded = () => {
    const video = videoRef.current;
    if (!video) return;

    video.pause();
    video.currentTime = 0;
    video.load();
    setIsVideoPlaying(false);
  };

  return (
    <div className="min-h-screen overflow-hidden bg-[#F8F7F4] dark:bg-surface">
      <header
        dir="ltr"
        className="mx-auto flex max-w-7xl items-center px-6 pt-10 sm:px-8 sm:pt-12 lg:px-0"
      >
        <Logo size="large" className="text-brand-logo !text-[2.75rem] tracking-[-0.06em]" />
        <div className="ms-auto flex items-center gap-2">
          <button
            type="button"
            aria-label={t("drawer.open")}
            aria-expanded={drawerOpen}
            aria-controls="product-drawer"
            onClick={() => setDrawerOpen(true)}
            className="grid size-11 place-items-center rounded-full border border-border bg-card text-foreground shadow-sm transition hover:-translate-y-0.5 hover:border-primary/25 hover:bg-primary-soft hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <Menu className="size-5" aria-hidden />
          </button>
          <AccountMenu />
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 pb-14 sm:px-8 lg:px-0">
        <section className="mt-14 grid items-start gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
          <div className="animate-fade-up">
            <h1 className="max-w-[640px] text-[2.8rem] leading-[1.01] font-extrabold tracking-[-0.055em] sm:text-[3.5rem]">
              <span className="block">{t("home.title.line1")}</span>
              <span className="block">
                {t("home.title.line2.prefix")}{" "}
                <span className="text-warm-highlight">{t("home.title.highlight")}</span>{" "}
                {t("home.title.line2.suffix")}
              </span>
            </h1>

            <p className="mt-6 max-w-[615px] text-base leading-[1.75] text-muted-foreground sm:text-[1.1rem]">
              {t("home.subtitle")}
            </p>

            <div className="mt-8">
              {managedCompanies ? <button
                type="button"
                onClick={returnToDashboard}
                className="inline-flex h-[52px] items-center justify-center gap-3 rounded-full bg-primary px-7 text-base font-bold text-primary-foreground shadow-sm transition hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                {t("home.backToDashboard")}
                <ArrowRight className="size-4 rtl:rotate-180" strokeWidth={2} aria-hidden />
              </button> : canOpenOnboarding && <>
                <p className="text-sm font-semibold text-foreground/80">{t("home.preview.label")}</p>
                <button
                  type="button"
                  aria-haspopup="dialog"
                  onClick={() => openOnboarding()}
                  className="mt-3 inline-flex h-[52px] items-center justify-center gap-3 rounded-full bg-primary px-7 text-base font-bold text-primary-foreground shadow-sm transition hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                >
                  {t("home.preview.cta")}
                  <ArrowRight className="size-4 rtl:rotate-180" strokeWidth={2} aria-hidden />
                </button>
              </>}
              <div aria-label="Qotta demos" className="mt-6 grid max-w-[600px] gap-3 sm:grid-cols-2">
                <DemoCard icon={<Sparkles className="size-4" aria-hidden />} title="Try the Customer Experience"
                  description="Experience what your website visitors will see.">
                  <button type="button" onClick={() => openDiscovery({ publicCustomerSlug: "demo", mode: "demo" })}
                    className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-foreground underline decoration-border-strong underline-offset-4 hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
                    Try Customer Demo <ArrowRight className="size-3.5" aria-hidden />
                  </button>
                </DemoCard>
                <DemoCard icon={<LayoutGrid className="size-4" aria-hidden />} title="View Dashboard Demo"
                  description="See the leads and insights you'll receive.">
                  <Link to="/app/$customerId" params={{ customerId: "demo" }} target="_blank" rel="noopener noreferrer"
                    className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-foreground underline decoration-border-strong underline-offset-4 hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
                    Explore Dashboard <ArrowRight className="size-3.5" aria-hidden />
                  </Link>
                </DemoCard>
              </div>
            </div>
          </div>

          <div className="group relative mt-1 aspect-[16/9] w-full animate-slide-in overflow-hidden rounded-[26px] bg-muted text-primary shadow-[0_22px_45px_-26px_var(--shadow-color-strong)] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary lg:mt-7">
            <video
              ref={videoRef}
              src="/videos/Woman_interacting_with_contact_form_202608161621.mp4"
              poster="/images/qotta-product-video-poster.jpg"
              preload="metadata"
              playsInline
              onEnded={handleVideoEnded}
              className="h-full w-full object-cover"
            />
            {!isVideoPlaying && (
              <>
                <span className="absolute inset-0 bg-foreground/5" aria-hidden />
                <button
                  type="button"
                  onClick={handleVideoPlay}
                  aria-label={t("home.video.play")}
                  className="absolute start-1/2 top-1/2 grid size-20 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white/95 shadow-lg transition hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary rtl:translate-x-1/2"
                >
                  <span className="discovery-progress grid size-14 place-items-center rounded-full text-white">
                    <Play className="ms-0.5 size-5 fill-none" strokeWidth={2} />
                  </span>
                </button>
              </>
            )}
          </div>
        </section>

        <section
          aria-label={t("home.values.label")}
          className="mx-auto mt-20 grid max-w-[1188px] gap-8 sm:grid-cols-3 lg:mt-[84px]"
        >
          <ValueStatement
            title={t("home.value.fit.title")}
            highlight={t("home.value.fit.highlight")}
            body={t("home.value.fit.body")}
          />
          <ValueStatement
            title={t("home.value.price.title")}
            highlight={t("home.value.price.highlight")}
            body={t("home.value.price.body")}
          />
          <ValueStatement
            title={t("home.value.context.title")}
            highlight={t("home.value.context.highlight")}
            body={t("home.value.context.body")}
          />
        </section>
      </main>

      {drawerOpen && <ProductDrawer onClose={() => setDrawerOpen(false)} hideAcquisition={Boolean(managedCompanies)} />}
    </div>
  );
}

function DemoCard({ icon, title, description, children }: { icon: ReactNode; title: string; description: string; children: ReactNode }) {
  return <article className="rounded-2xl border border-border bg-white/75 p-4 pb-2 dark:bg-card">
    <div className="flex items-center gap-2 text-muted-foreground">{icon}<h2 className="text-sm font-bold text-foreground">{title}</h2></div>
    <p className="mt-2 text-xs leading-5 text-muted-foreground">{description}</p>
    <div className="mt-1 flex">{children}</div>
  </article>;
}

function ValueStatement({
  title,
  highlight,
  body,
}: {
  title: string;
  highlight: string;
  body: string;
}) {
  return (
    <article className="w-full max-w-[270px] justify-self-center text-start">
      <h2 className="text-[1.3rem] leading-tight font-bold">
        <HighlightedText text={title} highlight={highlight} className="text-primary/70" />
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">{body}</p>
    </article>
  );
}
